import { Component, DestroyRef, inject, input, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@joii/design-system';
import { Subscription, switchMap, take } from 'rxjs';
import { ChatDataService } from 'src/shared/services/chat.data.service/chat.data.service';
import { ChatFacade } from 'src/shared/stores/chat/chat-facade.store';
import { JSONValue, vetaichatexternal } from 'src/shared/services/client/client';

/**
 * Component for rendering a generic chat button attachment.
 *
 * @component
 * @example
 * <app-generic-chat-button-attachment></app-generic-chat-button-attachment>
 *
 * @description
 * This component displays a button attachment within a chat interface, handling user interactions
 * and communicating with the chat API service. It manages the processing state to prevent duplicate
 * API calls and updates the chat store based on the result of the action. If the attachment action
 * is to end the chat, it collapses the chat widget.
 *
 * @inputs
 * - chatId: The unique identifier for the chat.
 * - messageId: The unique identifier for the message.
 * - attachment: The attachment data for the button.
 * - buttonStyleType: The style type of the button ('primary' | 'secondary').
 */
@Component({
  selector: 'app-generic-chat-button-attachment',
  imports: [ButtonComponent],
  templateUrl: './generic-chat-button-attachment.component.html',
  styleUrl: './generic-chat-button-attachment.component.scss',
})
export class GenericChatButtonAttachmentComponent implements OnDestroy {
  chatId = input.required<string>();
  messageId = input.required<string>();
  attachment = input.required<ChatButtonAttachment>();
  useDesignSystem = input<boolean>(false);
  buttonStyleType = input<'primary' | 'secondary'>('primary');
  processing = signal(false);

  private apiService = inject(ChatDataService);
  private chatStore = inject(ChatFacade);
  private destroyRef = inject(DestroyRef);
  private subscription = new Subscription();

  ngOnDestroy() {
    this.subscription.unsubscribe();
    // This is to ensure that if the button is removed while processing the api call then the chat widget is closed.
    // The api will remove the message and the button will not be visible anymore.
    if (this.processing() && this.attachment()?.id === 'endChat') {
      this.chatStore.ui.closeWidget();
    }
  }

  /**
   * Handles the click event for the chat button attachment.
   *
   * @remarks
   * This method prevents double clicks from triggering the API twice by checking the processing state.
   * If not already processing, it constructs the parameters for the chat call-to-action request,
   * sets the processing state to true, and subscribes to the API service callback.
   * On success, it updates the user chats and, if the attachment ID is 'endChat',
   * collapses the chat widget. On error or completion, it resets the processing state.
   * @public
   */
  onButtonClick() {
    if (this.processing()) return;
    const params: vetaichatexternal.ChatCallToActionRequest = {
      chat_doc_id: this.chatId(),
      message_id: this.messageId(),
      type: this.attachment().type,
      id: this.attachment().id,
      cta_callback_data: this.attachment().cta_callback_data,
    };
    this.processing.set(true);
    this.subscription.add(
      this.apiService
        .ChatCallToActionCallback(params)
        .pipe(
          switchMap(() => this.chatStore.connectUser()),
          take(1),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => {
            if (this.attachment().id === 'endChat') {
              this.chatStore.ui.closeWidget();
            }
          },
          error: (error) => {
            console.error(error);
            this.processing.set(false);
          },
          complete: () => {
            this.processing.set(false);
          },
        })
    );
  }
}

type ChatButtonAttachmentType = 'endChat' | 'vetPracticeReferralIgnore';

interface ChatButtonAttachment {
  id: ChatButtonAttachmentType;
  type: string;
  button_text: string;
  cta_callback_data: JSONValue;
}
